import { getDocs, query, where, orderBy, limit, startAfter, or, doc, getDoc, updateDoc, writeBatch, collection } from "firebase/firestore";
import { projectsDB, projectsSchemeDB, uploadAndSave, updateRecord } from "../../libs/firebase";
import { firebaseHelper, getActiveDesignFirmId } from "ui";
import { generateID, deepClone } from "../../libs/util";
import { BoardSchemeInterface, ProjectSchemeInterface, Permissions } from "ts-interfaces";
import { hasAccessToResource } from "ui/utils/usePermissions";
import { runMaskedQuery } from "./firestoreMaskedQuery";
import {
  inChunkSize,
  ownerRestFilter,
  teamScopeConstraints,
  teamScopeRestFilters,
  type TeamScope,
} from "./accessQueries";
import { freshAccessFields } from "./accessFields";

// A page of the workspace's boards: in a team, the ones the user can read
// (`teamScope`); in Personal, the user's own. `designFirmId` is unused since
// every team runs the sharing model.
export async function getBoardsScheme(
  lastDoc: any = null,
  user_id: string,
  pageSize = 15,
  _designFirmId: string | null = null,
  teamScope: TeamScope | null = null
): Promise<{ boards: BoardSchemeInterface[]; lastVisible: any }> {
  if (!projectsDB) return { boards: [], lastVisible: null };

  try {
    const scopeClauses = teamScope ? teamScopeConstraints(teamScope) : [where("user", "==", user_id)];
    const q = query(
      projectsDB,
      ...scopeClauses,
      orderBy("updatedAt", "desc"),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(pageSize)
    );
    
    const snapshot = await getDocs(q);
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    
    return {
      boards: snapshot.docs.map(doc => doc.data() as BoardSchemeInterface),
      lastVisible
    };
  } catch (error) {
    console.error('Error fetching user boards:', error);
    return { boards: [], lastVisible: null };
  }
}

/**
 * Boards for one project, letting failures propagate. List callers must use
 * this over `getBoardsForProject` below: a swallowed error returns [], which is
 * indistinguishable from "no boards" and paints an empty grid over a broken
 * query. Needs the (associatedProjectId ASC, updatedAt DESC) index on `projects`.
 */
export async function getBoardsForProjectOrThrow(
  projectId: string,
  user_id: string,
  permissions: Permissions | null,
  teamScope: TeamScope | null = null
): Promise<BoardSchemeInterface[]> {
  if (!projectsDB || !projectId) return [];

  const q = query(
    projectsDB,
    where("associatedProjectId", "==", projectId),
    // Outside the active team only the user's own boards are readable (Personal
    // is owner-only); needs (associatedProjectId, user, updatedAt DESC).
    ...(teamScope ? teamScopeConstraints(teamScope) : [where("user", "==", user_id)]),
    orderBy("updatedAt", "desc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.data() as BoardSchemeInterface);
}

export async function getBoardsForProject(
  projectId: string,
  user_id: string,
  permissions: Permissions | null,
  teamScope: TeamScope | null = null
): Promise<BoardSchemeInterface[]> {
  try {
    return await getBoardsForProjectOrThrow(projectId, user_id, permissions, teamScope);
  } catch (error) {
    console.error('Error fetching boards for project:', error);
    return [];
  }
}

// Everything the project list store's readers use (sidebar counts, project
// card thumbnails, the onboarding checklist, analytics). Boards fetched with
// this mask carry no physicalObjects, so they must never be written back.
export const BOARD_SUMMARY_FIELDS = [
  "id",
  "name",
  "user",
  "associatedProjectId",
  "design_firm_id",
  "updatedAt",
  "createdAt",
  "archive",
  "seededFromTemplate",
  "created_by",
  "access",
  "readers",
  "editors",
  "files.screenshot",
  "projectData.title",
] as const;

const SUMMARY_QUERY_TIMEOUT_MS = 12_000;

async function getBoardSummariesChunk(
  ids: string[],
  user_id: string,
  teamScope: TeamScope | null = null
): Promise<BoardSchemeInterface[]> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Bounds the whole attempt, token refresh included, not just the fetch.
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error("Board summary query timed out"));
    }, SUMMARY_QUERY_TIMEOUT_MS);
  });
  try {
    const summaries = runMaskedQuery({
      collectionId: "projects",
      fields: BOARD_SUMMARY_FIELDS,
      inField: "associatedProjectId",
      inValues: ids,
      orderByDesc: "updatedAt",
      // Outside the active team only the user's own boards are readable.
      andFilters: teamScope ? teamScopeRestFilters(teamScope) : ownerRestFilter("user", user_id),
      signal: controller.signal,
    });
    return (await Promise.race([summaries, timeout])) as BoardSchemeInterface[];
  } catch (error) {
    console.warn("Board summary query failed; falling back to full boards:", error);
    const snapshot = await getDocs(
      query(
        projectsDB,
        where("associatedProjectId", "in", ids),
        ...(teamScope ? teamScopeConstraints(teamScope) : [where("user", "==", user_id)]),
        orderBy("updatedAt", "desc")
      )
    );
    // Same shape as the REST path, which fills a missing id from the doc name.
    return snapshot.docs.map(doc => {
      const data = doc.data() as BoardSchemeInterface;
      return { ...data, id: data.id ?? doc.id };
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Board summaries (BOARD_SUMMARY_FIELDS only) for every project, grouped by
 * project. Full board lists for one project come from getBoardsForProject.
 */
export async function getBoardsForProjects(
  projectIds: string[],
  user_id: string,
  permissions: Permissions | null,
  teamScope: TeamScope | null = null
): Promise<Map<string, BoardSchemeInterface[]>> {
  if (!projectsDB || projectIds.length === 0) return new Map();

  try {
    const boardsByProject = new Map<string, BoardSchemeInterface[]>();

    // Initialize map with empty arrays
    projectIds.forEach(id => boardsByProject.set(id, []));

    // Chunks of 30 (Firestore `in` limit; 15 under a team scope), all in
    // flight at once (#3924).
    const size = inChunkSize(teamScope);
    const chunks: Promise<BoardSchemeInterface[]>[] = [];
    for (let i = 0; i < projectIds.length; i += size) {
      chunks.push(getBoardSummariesChunk(projectIds.slice(i, i + size), user_id, teamScope));
    }
    const results = await Promise.all(chunks);

    results.flat().forEach(board => {
      if (board.associatedProjectId) {
        const projectBoards = boardsByProject.get(board.associatedProjectId) || [];
        projectBoards.push(board);
        boardsByProject.set(board.associatedProjectId, projectBoards);
      }
    });

    return boardsByProject;
  } catch (error) {
    console.error('Error fetching boards for projects:', error);
    return new Map();
  }
}

export async function renameBoard(boardId: string, newName: string, user_id:string, permissions: Permissions | null): Promise<void> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions, 'read', true);
  if (!docSnap) {
    throw new Error("No access to the board");
  }
  if ( !newName || newName?.length < 1 ) throw new Error("Invalid board name");
  await updateDoc(docSnap.ref, {"projectData.title": newName,updatedAt: Date.now()});
}

export async function copyBoard(board: BoardSchemeInterface, user_id:string, permissions: Permissions | null): Promise<BoardSchemeInterface> {
  if (!user_id) throw new Error("User not authenticated");
  // Clone the stored board, not `board`: a list store may hold a summary
  // (BOARD_SUMMARY_FIELDS) without the scene.
  const docSnap = await getDoc(doc(firebaseHelper.db, "projects", board.id));
  if (!docSnap.exists()) throw new Error("Board not found");
  const newId = generateID()
  const cloneBoard = deepClone(docSnap.data() as BoardSchemeInterface)
  cloneBoard.id=newId;
  cloneBoard.projectData.projectId=newId
  cloneBoard.projectData.title = "Copy of " + cloneBoard.projectData.title
  cloneBoard.createdAt = Date.now();
  cloneBoard.updatedAt = Date.now();
  // Reset these fields for the duplicate
  cloneBoard.associatedProjectId = null;
  cloneBoard.projectData.associatedProjectId = null; // Also clear the nested one
  cloneBoard.projectData.userId = user_id;
  cloneBoard.user = user_id;
  cloneBoard.writeAccess= []; // Set to empty array as specified
  Object.assign(cloneBoard, freshAccessFields(user_id));
  // Stamp the copy with the user's current active workspace, not the
  // source board's. Duplicating a team board into Personal moves the
  // copy into Personal (Phase A.3.2).
  cloneBoard.design_firm_id = getActiveDesignFirmId(user_id);
  const duplicatedBoard: any = await uploadAndSave(cloneBoard.id,'projects',user_id, cloneBoard)
  return duplicatedBoard;
}

export async function copyBoardFromId(boardId: string, user_id:string, permissions: Permissions | null): Promise<BoardSchemeInterface> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions);
  if (!docSnap) {
    throw new Error("No access to the board");
  }
  const board = docSnap.data() as BoardSchemeInterface;
  const newId = generateID()
  const cloneBoard = deepClone(board)
  cloneBoard.id=newId;
  cloneBoard.projectData.projectId=newId
  cloneBoard.projectData.title = "Copy of " + cloneBoard.projectData.title
  cloneBoard.createdAt = Date.now();
  cloneBoard.updatedAt = Date.now();
  // Reset these fields for the duplicate
  cloneBoard.associatedProjectId = null;
  cloneBoard.projectData.associatedProjectId = null; // Also clear the nested one
  cloneBoard.projectData.userId = user_id;
  cloneBoard.user = user_id;
  cloneBoard.writeAccess= []; // Set to empty array as specified
  Object.assign(cloneBoard, freshAccessFields(user_id));
  // Stamp the copy with the user's current active workspace (Phase A.3.2).
  cloneBoard.design_firm_id = getActiveDesignFirmId(user_id);
  const duplicatedBoard: any = await uploadAndSave(cloneBoard.id,'projects',user_id, cloneBoard)
  return duplicatedBoard;
}

// Archiving is a write: a team board's editors (its project's people come
// through them), a Personal board's owner, as the rules have it.
export const canArchiveBoard = (board: any, uid: string | undefined, permissions: Permissions | null): boolean =>
  hasAccessToResource(permissions, board.id, 'write', 'board', board.user || '', uid || '', null, board);

export async function deleteBoard(boardId: string,user_id:string, permissions: Permissions | null): Promise<void> {
  const snap = await getDoc(doc(firebaseHelper.db, "projects", boardId));
  if (!snap.exists() || !canArchiveBoard({ ...snap.data(), id: boardId }, user_id, permissions)) {
    throw new Error("You don't have permission to archive this board");
  }
  await updateRecord(boardId, 'projects', {archive:true});
}

// Un-archive (restore) a soft-deleted board — mirror of deleteBoard in reverse.
// Restore is a mutation, so it requires write access (owner or boardWrite ACL).
export async function restoreBoard(boardId: string, user_id: string, permissions: Permissions | null): Promise<void> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions, 'write');
  if (!docSnap) {
    throw new Error("No access to the board");
  }
  await updateRecord(boardId, 'projects', { archive: false, updatedAt: Date.now() });
}

// Hard-delete an archived board and its versions in one batch. Owner-only, as
// the projects delete rule is. Storage files stay: copies of a board share its
// screenshot URLs.
export async function permanentlyDeleteBoard(boardId: string, user_id: string): Promise<void> {
  if (!user_id) throw new Error("User not authenticated");
  const boardRef = doc(firebaseHelper.db, "projects", boardId);
  const docSnap = await getDoc(boardRef);
  if (!docSnap.exists()) throw new Error("Board not found");
  const board = docSnap.data();
  if (board.user !== user_id) throw new Error("Only the board owner can delete it");
  if (!board.archive) throw new Error("Archive the board before deleting it");

  const versions = await getDocs(collection(firebaseHelper.db, "projects", boardId, "versions"));
  const batch = writeBatch(firebaseHelper.db);
  versions.docs.forEach((version) => batch.delete(version.ref));
  batch.delete(boardRef);
  await batch.commit();
}

/**
 * Read one board with the standard access check (owner / board ACL / team
 * membership via design_firm_id — the same rule the schedules API applies).
 * Returns null when the board is missing or unreadable. Added for the
 * read-only My Mattoboard board detail (Home/mine); additive.
 */
export async function getBoardById(
  boardId: string,
  user_id: string,
  permissions: Permissions | null,
): Promise<BoardSchemeInterface | null> {
  if (!user_id || !boardId) return null;
  try {
    const docSnap = await getDoc(doc(firebaseHelper.db, "projects", boardId));
    if (!docSnap.exists()) return null;
    const boardData = docSnap.data() as BoardSchemeInterface;
    const hasAccess = hasAccessToResource(
      permissions,
      boardId,
      "read",
      "board",
      boardData.user || "",
      user_id,
      boardData.design_firm_id ?? null,
      boardData,
    );
    return hasAccess ? boardData : null;
  } catch {
    return null;
  }
}

// `mutation`: a write behind this read gate. Under sharingV2 every board is
// readable by link, so it asks for edit access, as the rules will.
async function getBoardIfAccessible(boardId: string, user_id:string, permissions:Permissions | null, accessType: 'read' | 'write' = 'read', mutation = false)  {
  if (!user_id) throw new Error("User not authenticated");
  if (!boardId) throw new Error("Invalid board ID");
  
  try {
    const docSnap = await getDoc(doc(firebaseHelper.db, "projects", boardId));
    if (!docSnap.exists()) throw new Error("Board not found");
    const boardData = docSnap.data();  
    // A mutation needs write: every board is readable by link.
    const type = mutation ? 'write' : accessType;
    const hasAccess = hasAccessToResource(permissions, boardId, type, 'board', boardData.user || '', user_id, null, boardData);
    // if (boardData.user == user_id) return docSnap;
    // if (!permissions) return null;
    // const hasAccess = permissions.boardRead?.includes(boardId) || permissions.boardWrite?.includes(boardId);
    if (hasAccess) return docSnap
    return null;
  }
  catch {
    throw new Error("Board not found or no access");
  }

}

export async function associateBoardWithProject(
  boardId: string, 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<BoardSchemeInterface> {
  const docSnap = await getBoardIfAccessible(boardId, user_id, permissions, 'read', true);
  if (!docSnap) {
    throw new Error("No access to the board");
  }
  
  const currentBoardData = docSnap.data() as BoardSchemeInterface;

  // Filing is pointer-only: since the cutover a project's people reach a
  // team board through its readers/editors, and writeAccess grants nothing.
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id, projectData.design_firm_id ?? null, projectData);
    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
  }

  // Update the board
  const updatedAt = Date.now();
  await updateDoc(docSnap.ref, {
    'projectData.associatedProjectId': projectId,
    associatedProjectId: projectId,
    updatedAt
  });

  // The stored board as it now is, for callers that update local stores.
  return {
    ...currentBoardData,
    id: boardId,
    associatedProjectId: projectId,
    projectData: { ...currentBoardData.projectData, associatedProjectId: projectId },
    updatedAt,
  } as BoardSchemeInterface;
}

export async function associateMultipleBoardsWithProject(
  boardsToAdd: string[], 
  boardsToRemove: string[], 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  // Validate project access first if projectId is provided
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id, projectData.design_firm_id ?? null, projectData);
    
    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
  }

  // Process all boards in parallel
  const allBoardIds = [...boardsToAdd, ...boardsToRemove];
  const boardPromises = allBoardIds.map(async (boardId) => {
    try {
      if (boardsToAdd.includes(boardId)) {
        // Associate with project
        await associateBoardWithProject(boardId, projectId, user_id, permissions);
      } else {
        // Remove from project (set to null)
        await associateBoardWithProject(boardId, null, user_id, permissions);
      }
    } catch (error) {
      console.error(`Error updating board ${boardId}:`, error);
      throw error;
    }
  });

  await Promise.all(boardPromises);
}

